import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ROUTER_KEYS } from "@/router/routers";
import { RootState } from "@/store";
import { User, UserPermissionEnum } from "@/types/user";
import { hasPermission } from "@/utils/user";
import { PageId } from "@/types/page";
import { DictContext } from "@/layout/context/DictContext";

export interface NavTitle {
  key: ROUTER_KEYS | string;
  label: string;
  children?: Array<NavTitle>;
  hasPermission?: boolean;
}

type UseNav = () => Array<NavTitle>;

const useNav: UseNav = () => {
  const { t } = useTranslation();
  const curUser: User = useSelector((state: RootState) => state.userReducer.curUser);
  const { rootCategories } = useContext(DictContext)

  const menuNavTitles: NavTitle[] = useMemo(
    () => [
      {
        key: ROUTER_KEYS.SALES,
        label: t("layout.salesMana"),
        hasPermission: hasPermission(curUser, [
          UserPermissionEnum.SALE_REVIEW,
          UserPermissionEnum.SALE_OK_ORDER,
          UserPermissionEnum.SALE_CUSTOMER,
        ]),
        children: [
          {
            key: `${ROUTER_KEYS.SALES_ORDER}?pageId=${PageId.SALE_NOT_PASS}`,
            label: t("layout.orderRe"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.SALE_REVIEW]),
          },
          {
            key: `${ROUTER_KEYS.SALES_ORDER}?pageId=${PageId.SALE_OFFICIAL}`,
            label: t("layout.okOrder"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.SALE_OK_ORDER]),
          },
          {
            key: `${ROUTER_KEYS.SALES_CUSTOMER}?pageId=${PageId.SALE_CUSTOMER}`,
            label: t("layout.customMana"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.SALE_CUSTOMER]),
          },
        ],
      },
      {
        key: ROUTER_KEYS.MATERIAL,
        label: t("layout.materialControl"),
        hasPermission: hasPermission(curUser, [
          UserPermissionEnum.MC_REQUIREMENT,
          UserPermissionEnum.MC_OK_ORDER,
          UserPermissionEnum.MC_BOM,
          UserPermissionEnum.REQ_MANAGE,
          UserPermissionEnum.MC_REQ_M,
        ]),
        children: [
          {
            key: "mc-waitOk",
            label: t("layout.waitOk"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.MC_REQUIREMENT, UserPermissionEnum.REQ_MANAGE,]),
            children: [
              {
                key: `${ROUTER_KEYS.SALES_ORDER}?pageId=${PageId.MC_NOT_PASS}`,
                label: t("layout.noPass"),
                hasPermission: true,
              },
              {
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.MC_PASSED}`,
                label: t("layout.passed"),
                hasPermission: true
              },
              {
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.MC_BE_REJECT}`,
                label: t("layout.reject"),
                hasPermission: true
              },
              {
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.MC_REQ_MANAGE}`,
                label: t("bom.reqManage"),
                hasPermission: hasPermission(curUser, [UserPermissionEnum.REQ_MANAGE])
              },
              {
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.MC_REQ_M}`,
                label: t("layout.reqM"),
                hasPermission: hasPermission(curUser, [UserPermissionEnum.MC_REQ_M])
              },
            ],
          },
          {
            label: t("layout.orderTrack"),
            key: 'mc-ok-order',
            hasPermission: false && hasPermission(curUser, [UserPermissionEnum.MC_OK_ORDER]),
            children: [
              {
                label: t("layout.pendingOrder"),
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.MC_OFFICIAL_PENDING}`,
                hasPermission: true
              },
              {
                label: t("layout.doneOrder"),
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.MC_OFFICIAL_DONE}`,
                hasPermission: true
              },
              {
                label: t("layout.closeOrder"),
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.MC_OFFICIAL_CLOSED}`,
                hasPermission: true
              },
            ],
          },
          {
            key: "mc-maintenance",
            label: t("layout.mcDataMt"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.MC_BOM]),
            children: [
              {
                key: `${ROUTER_KEYS.BOM}?pageId=${PageId.MC_BOM}`,
                label: t("layout.mcReTemp"),
                hasPermission: hasPermission(curUser, [UserPermissionEnum.MC_BOM]),
              },
              {
                key: `${ROUTER_KEYS.MATERIAL_INVENTORY_ALERT}?pageId=${PageId.MATERIAL_INVENTORY_ALERT}`,
                label: t("layout.inventoryAlert"),
                hasPermission: hasPermission(curUser, [UserPermissionEnum.MATERIAL_INVENTORY_ALERT]),
              },
            ],
          },
        ],
      },
      {
        key: "commodity",
        label: t("layout.commodity"),
        hasPermission: hasPermission(curUser, [UserPermissionEnum.CMD_REQUIREMENT, UserPermissionEnum.CMD_OK_ORDER, UserPermissionEnum.SALES_PRODUCT_INFO]),
        children: [
          {
            key: "commodity-waitOk",
            label: t("layout.waitOk"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.CMD_REQUIREMENT]),
            children: [
              {
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.COMMODITY_NOT_PASS}`,
                label: t("layout.noPass"),
                hasPermission: true,
              },
              {
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.COMMODITY_PASSED}`,
                label: t("layout.passed"),
                hasPermission: true,
              },
              {
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.COMMODITY_BE_REJECT}`,
                label: t("layout.reject"),
                hasPermission: true,
              },
            ],
          },
          {
            label: t("layout.orderTrack"),
            key: 'prod-ok-order',
            hasPermission: false && hasPermission(curUser, [UserPermissionEnum.CMD_OK_ORDER]),
            children: [
              {
                label: t("layout.pendingOrder"),
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.COMMODITY_OFFICIAL_PENDING}`,
                hasPermission: true
              },
              {
                label: t("layout.doneOrder"),
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.COMMODITY_OFFICIAL_DONE}`,
                hasPermission: true
              },
              {
                label: t("layout.closeOrder"),
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.COMMODITY_OFFICIAL_CLOSED}`,
                hasPermission: true
              },
            ],
          },
          {
            key: `${ROUTER_KEYS.SALES_PRODUCT_INFO}?pageId=${PageId.SALES_PRODUCT_INFO}`,
            label: t("layout.productionInfo"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.SALES_PRODUCT_INFO]),
          },
        ],
      },
      {
        key: "prod",
        label: t("layout.prod"),
        hasPermission: hasPermission(curUser, [
          UserPermissionEnum.PR_REQUIREMENT,
          UserPermissionEnum.PR_OK_ORDER,
          UserPermissionEnum.PR_REPORT_ENTRY,
          UserPermissionEnum.PR_REPORT_MT,
          UserPermissionEnum.FAC_INVENTORY,
        ]),
        children: [
          {
            key: "prod-waitOk",
            label: t("layout.waitOk"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.PR_REQUIREMENT]),
            children: [
              {
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.PROD_NOT_PASS}`,
                label: t("layout.noPass"),
                hasPermission: true,
              },
              {
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.PROD_PASSED}`,
                label: t("layout.passed"),
                hasPermission: true,
              },
              {
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.PROD_BE_REJECT}`,
                label: t("layout.reject"),
                hasPermission: true,
              },
            ],
          },
          {
            key: `${ROUTER_KEYS.PROD_MATERIAL_OVER_ISSUE}?pageId=${PageId.PROD_MATERIAL_OVER_ISSUE}`,
            label: "超领申请",
            hasPermission: hasPermission(curUser, [UserPermissionEnum.PR_REQUIREMENT]),
          },
          {
            label: t("layout.orderTrack"),
            key: 'prod-ok-order',
            hasPermission: false && hasPermission(curUser, [UserPermissionEnum.PR_OK_ORDER]),
            children: [
              {
                label: t("layout.pendingOrder"),
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.PROD_OFFICIAL_PENDING}`,
                hasPermission: true
              },
              {
                label: t("layout.doneOrder"),
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.PROD_OFFICIAL_DONE}`,
                hasPermission: true
              },
              {
                label: t("layout.closeOrder"),
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.PROD_OFFICIAL_CLOSED}`,
                hasPermission: true
              },
            ],
          },
          {
            key: `${ROUTER_KEYS.FAC_INVENTORY}?pageId=${PageId.FAC_INVENTORY}`,
            label: t("layout.factoryInventory"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.FAC_INVENTORY]),
          },
          {
            key: `${ROUTER_KEYS.SALES_DATA}?pageId=${PageId.PROD_ENTER_DATA}`,
            label: t("layout.orderEntry"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.PR_REPORT_ENTRY]),
          },
          {
            key: `${ROUTER_KEYS.SALES_DATA_MAINTAIN}?pageId=${PageId.PROD_DATA_MT}`,
            label: t("layout.orderProdMt"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.PR_REPORT_MT]),
          },
        ],
      },
      {
        key: ROUTER_KEYS.PURCHASE,
        label: t("layout.purchaseMana"),
        hasPermission: hasPermission(curUser, [
          UserPermissionEnum.PUR_REQUIREMENT,
          UserPermissionEnum.PUR_OK_ORDER,
          UserPermissionEnum.PUR_SUPPLIER,
          UserPermissionEnum.PUR_ORDER,
          UserPermissionEnum.PUR_REQUIREMENT_ORDER,
          UserPermissionEnum.PURCHASE_OPTION,
          UserPermissionEnum.PURCHASE_OPTION_MANUAL,
          UserPermissionEnum.PURCHASE_RETURN,
        ]),
        children: [
          {
            key: "purchase-waitOk",
            label: t("layout.waitOk"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.PUR_REQUIREMENT]),
            children: [
              {
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.PUR_NOT_PASS}`,
                label: t("layout.noPass"),
                hasPermission: true,
              },
              {
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.PUR_PASSED}`,
                label: t("layout.passed"),
                hasPermission: true,
              },
              {
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.PUR_BE_REJECT}`,
                label: t("layout.reject"),
                hasPermission: true,
              },
              {
                key: `${ROUTER_KEYS.QUOTATION_LIST}?pageId=${PageId.PUR_ALL_QUTATION}`,
                label: t("layout.QutationManage"),
                hasPermission: true,
              },
            ],
          },
          {
            label: t("layout.purOrder"),
            key: 'pur-order',
            hasPermission: hasPermission(curUser, [UserPermissionEnum.PUR_ORDER]),
            children: [
              {
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.PUR_ALL_PASSED}`,
                label: t("layout.requirementPurchase"),
                hasPermission: true,
              },
              {
                label: t("layout.purPendingPay"),
                key: `${ROUTER_KEYS.PURCHASE_ORDER}?pageId=${PageId.PUR_ORDER_PENDING_PAY}`,
                hasPermission: true
              },
              {
                label: t("layout.purPayed"),
                key: `${ROUTER_KEYS.PURCHASE_ORDER}?pageId=${PageId.PUR_ORDER_PAYED}`,
                hasPermission: true
              },
              {
                label: t("layout.purDone"),
                key: `${ROUTER_KEYS.PURCHASE_ORDER}?pageId=${PageId.PUR_ORDER_DONE}`,
                hasPermission: true
              },
              {
                label: t("purchase.helpAcc"),
                key: `${ROUTER_KEYS.PURCHASE_ORDER}?pageId=${PageId.PUR_HELP_ACCEPT}`,
                hasPermission: hasPermission(curUser, [UserPermissionEnum.PUR_HELP_ACCEPT])
              },
              {
                label: t("purchase.zfed"),
                key: `${ROUTER_KEYS.PURCHASE_ORDER}?pageId=${PageId.PUR_ZF}`,
                hasPermission: true
              },
              {
                key: `${ROUTER_KEYS.PUR_INV_ITEM}?pageId=${PageId.PUR_INV_ITEM}`,
                label: t("layout.purInvItem"),
                hasPermission: hasPermission(curUser, [UserPermissionEnum.PUR_INV_ITEM]),
              },
            ],
          },
          {
            label: t("layout.orderTrack"),
            key: 'pur-ok-order',
            hasPermission: false && hasPermission(curUser, [UserPermissionEnum.PUR_OK_ORDER]),
            children: [
              {
                label: t("layout.pendingOrder"),
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.PUR_OFFICIAL_PENDING}`,
                hasPermission: true
              },
              {
                label: t("layout.doneOrder"),
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.PUR_OFFICIAL_DONE}`,
                hasPermission: true
              },
              {
                label: t("layout.closeOrder"),
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.PUR_OFFICIAL_CLOSED}`,
                hasPermission: true
              },
            ],
          },
          {
            key: "pur-maintenance",
            label: t("layout.purOrderMt"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.PUR_SUPPLIER]),
            children: [
              {
                key: `${ROUTER_KEYS.PURCHASE_SUPPLIER}?pageId=${PageId.PUR_SUPPLIER}`,
                label: t("layout.supplierMana"),
                hasPermission: hasPermission(curUser, [UserPermissionEnum.PUR_SUPPLIER,]),
              },
              {
                key: `${ROUTER_KEYS.PURCHASE_PRICES}?pageId=${PageId.PUR_PRICES}`,
                label: t("layout.priceList"),
                hasPermission: hasPermission(curUser, [UserPermissionEnum.PUR_PRICES,]),
              },
              // {
              //   key: `${ROUTER_KEYS.PURCHASE_DIS_SUPPLIER}?pageId=${PageId.PUR_DIS_SUPPLIER}`,
              //   label: t("layout.disabledSupplier"),
              //   hasPermission: hasPermission(curUser, [UserPermissionEnum.PUR_SUPPLIER,]),
              // },
              {
                key: `${ROUTER_KEYS.PURCHASE_CATEGORY}?pageId=${PageId.PUR_SUPPLIER}`,
                label: t("layout.gategoryManage"),
                hasPermission: hasPermission(curUser, [UserPermissionEnum.PUR_QUO_PRO_LIST,]),
              },
              {
                key: `${ROUTER_KEYS.PRICE_MANAGE}?pageId=${PageId.PUR_SUPPLIER}`,
                label: t("layout.quotationManage"),
                hasPermission: hasPermission(curUser, [UserPermissionEnum.PUR_QUO_MAN,]),
              },
            ],
          },
          {
            key: ROUTER_KEYS.PURCHASE_OPTION,
            label: t("layout.purchaseOperation"),
            hasPermission: hasPermission(curUser, [
              UserPermissionEnum.PURCHASE_OPTION_MANUAL,
              UserPermissionEnum.PURCHASE_MATERIAL_TRACK,
              UserPermissionEnum.PURCHASE_RETURN,]),
            children: [
              {
                key: `${ROUTER_KEYS.PURCHASE_OPTION_MANUAL}?pageId=${PageId.PUR_OM}`,
                label: t("layout.manualQuotation"),
                hasPermission: hasPermission(curUser, [UserPermissionEnum.PURCHASE_OPTION_MANUAL,]),
              },
              {
                key: `${ROUTER_KEYS.PURCHASE_MATERIAL_TRACK}?pageId=${PageId.PUR_MT}`,
                label: t("layout.materialTracking"),
                hasPermission: hasPermission(curUser, [UserPermissionEnum.PURCHASE_MATERIAL_TRACK,]),
              },
              {
                key: `${ROUTER_KEYS.PURCHASE_RETURN}?pageId=${PageId.PUR_RT}`,
                label: t("layout.returnAndExchange"),
                hasPermission: hasPermission(curUser, [UserPermissionEnum.PURCHASE_RETURN,]),
              },
            ],
          },

        ],
      },
      {
        key: "finance",
        label: t("layout.finance"),
        hasPermission: hasPermission(curUser, [
          UserPermissionEnum.FINANCE_REQUIREMENT,
          UserPermissionEnum.FINANCE_OK_ORDER,
          UserPermissionEnum.FINANCE_SALE_ORDER_FINISH,
        ]),
        children: [
          {
            key: "finance-waitOk",
            label: t("layout.waitOk"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.FINANCE_REQUIREMENT]),
            children: [
              {
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.F_NOT_PASS}`,
                label: t("layout.noPass"),
                hasPermission: true,
              },
              {
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.F_PASSED}`,
                label: t("layout.passed"),
                hasPermission: true,
              },
              {
                key: `${ROUTER_KEYS.REQUIREMENT_LIST}?pageId=${PageId.F_BE_REJECT}`,
                label: t("layout.reject"),
                hasPermission: true,
              },
            ],
          },
          {
            label: t("layout.orderTrack"),
            key: 'finance-ok-order',
            hasPermission: false && hasPermission(curUser, [UserPermissionEnum.FINANCE_OK_ORDER]),
            children: [
              {
                label: t("layout.pendingOrder"),
                key: `${ROUTER_KEYS.SALES_ORDER}?pageId=${PageId.F_OFFICIAL_PENDING}`,
                hasPermission: true
              },
              {
                label: t("layout.doneOrder"),
                key: `${ROUTER_KEYS.SALES_ORDER}?pageId=${PageId.F_OFFICIAL_DONE}`,
                hasPermission: true
              },
              {
                label: t("layout.closeOrder"),
                key: `${ROUTER_KEYS.SALES_ORDER}?pageId=${PageId.F_OFFICIAL_CLOSED}`,
                hasPermission: true
              },
            ],
          },
          {
            key: `${ROUTER_KEYS.SALES_ORDER}?pageId=${PageId.F_SALE_OFFICIAL}`,
            label: t("layout.saleOrderFinish"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.FINANCE_SALE_ORDER_FINISH]),
          },
        ],
      },
      {
        key: ROUTER_KEYS.WAREHOUSE,
        label: t("layout.invnManag"),
        hasPermission: hasPermission(curUser,
          [
            UserPermissionEnum.STORE_LIST,
            UserPermissionEnum.STORE_WAREHOUSE_PREPARATION,
            UserPermissionEnum.STORE_WAREHOUSE_MANAGEMENT,
            UserPermissionEnum.WAREHOUSE_INVENTORY_DETAIL,
            UserPermissionEnum.LOC_INV_DTL,
            UserPermissionEnum.STORE_ORDER,
            UserPermissionEnum.STORE_LOCATION_MANAGEMENT,
            UserPermissionEnum.STORE_LOCATION_REF_PRODUCTS,
            UserPermissionEnum.STORE_COUNTING_HISTORY,
            UserPermissionEnum.WAREHOUSE_IQC,
            UserPermissionEnum.ACT_STORE_IN,
            UserPermissionEnum.ACT_STORE_OUT,
            UserPermissionEnum.PACKING,
            UserPermissionEnum.INSPECTION,
            UserPermissionEnum.INSP_ITEM,
          ]),
        children: [
          {
            key: ROUTER_KEYS.WAREHOUSE_ORDERMANAGE,
            label: t("layout.warehousePreparation"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.STORE_WAREHOUSE_PREPARATION]),
          },
          {
            key: ROUTER_KEYS.WAREHOUSE_INVENTORY,
            label: t("layout.warehouseManagement"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.STORE_WAREHOUSE_MANAGEMENT]),
          },
          {
            key: `${ROUTER_KEYS.WAREHOUSE_INVENTORY_DETAIL}?pageId=${PageId.WAREHOUSE_INVENTORY_DETAIL}`,
            label: t("layout.inOutDetail"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.WAREHOUSE_INVENTORY_DETAIL]),
          },
          {
            key: `${ROUTER_KEYS.LOC_INV_DTL}?pageId=${PageId.LOC_INV_DTL}`,
            label: t("layout.locInvDetail"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.LOC_INV_DTL]),
          },
          {
            key: ROUTER_KEYS.WAREHOUSE_ORDER,
            label: t("layout.operationStatus"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.STORE_ORDER]),
          },
          {
            key: ROUTER_KEYS.WAREHOUSE_INVENTORY_MANAGE,
            label: t("layout.locationManagement"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.STORE_LOCATION_MANAGEMENT]),
          },
          {
            key: ROUTER_KEYS.WAREHOUSE_LOCATION_REF_PRODUCTS,
            label: t("layout.locationRefProducts"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.STORE_LOCATION_REF_PRODUCTS]),
          },
          {
            key: ROUTER_KEYS.WAREHOUSE_COUNTING,
            label: t("layout.invnHistory"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.STORE_COUNTING_HISTORY]),
          },
          {
            key: `${ROUTER_KEYS.WAREHOUSE_IQC}?pageId=${PageId.WAREHOUSE_IQC}`,
            label: t("warehouse.iqcList"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.WAREHOUSE_IQC]),
          },
          {
            key: `${ROUTER_KEYS.PACKING}?pageId=${PageId.PACKING}`,
            label: t("warehouse.packing"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.PACKING]),
          },
          {
            key: `${ROUTER_KEYS.INSPECTION}?pageId=${PageId.INSPECTION}`,
            label: t("inspection.inspection"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.INSPECTION]),
          },
          {
            key: `${ROUTER_KEYS.INSPECTION_ITEM}?pageId=${PageId.INSPECTION_ITEM}`,
            label: t("inspection.inspectItem"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.INSP_ITEM]),
          },
        ]
      },
      {
        key: ROUTER_KEYS.SHIPMENT,
        label: t("shipment.shipmentMana"),
        hasPermission: hasPermission(curUser, [UserPermissionEnum.SHIPMENT_LIST]),
        children: [
          {
            key: `${ROUTER_KEYS.SHIPMENT_LIST}?pageId=${PageId.SHIPMENT_LIST}`,
            label: t("shipment.shipmentListPage"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.SHIPMENT_LIST]),
          },
        ],
      },
      {
        key: ROUTER_KEYS.DATA,
        label: t("layout.dataModule"),
        hasPermission: hasPermission(curUser, [UserPermissionEnum.DATA_ORDER_REPORT]),
        children: [
          {
            key: `${ROUTER_KEYS.DATA_REPORTS}?pageId=${PageId.DATA_PROD_DATA}`,
            label: t("layout.orderReport"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.DATA_ORDER_REPORT]),
          },
        ],
      },
      {
        key: `${ROUTER_KEYS.ORG_MANAGER}?pageId=${PageId.ORG_MANA}`,
        label: t("layout.orgManag"),
        hasPermission: hasPermission(curUser, [UserPermissionEnum.ORG_PREVIEW]),
      },
      {
        key: ROUTER_KEYS.USER_MANAGER,
        label: t("layout.memberManag"),
        hasPermission: hasPermission(curUser, [UserPermissionEnum.MEMBER_LIST]),
        children: [
          {
            key: `${ROUTER_KEYS.USER_ENABLED_MANAGER}?pageId=${PageId.MEMBER_ENABLED}`,
            label: t("layout.enableMember"),
            hasPermission: true
          },
          {
            key: `${ROUTER_KEYS.USER_DISABLED_MANAGER}?pageId=${PageId.MEMBER_DISABLED}`,
            label: t("layout.disableMember"),
            hasPermission: true
          },
        ],
      },
      {
        key: `${ROUTER_KEYS.STORE_PRODUCT_CATEGORY_MANAGER}?pageId=${PageId.PRODUCT_CATEGORY}`,
        label: t("layout.categoryManag"),
        hasPermission: hasPermission(curUser, [UserPermissionEnum.PRODUCT_ADD_CATEGORY_LIST]),
      },
      {
        key: `${ROUTER_KEYS.STORE_PRODUCT_MANAGER}?pageId=${PageId.PRODUCT_LIST}`,
        label: t("layout.productList"),
        hasPermission: hasPermission(curUser, [UserPermissionEnum.PRODUCT_LIST]),
      },
      {
        key: `${ROUTER_KEYS.APP_MANAGER}?pageId=${PageId.APP_LIST}`,
        label: t("layout.APPManagement"),
        hasPermission: false && hasPermission(curUser, [UserPermissionEnum.APP_LIST]),
      },
      {
        key: ROUTER_KEYS.DATA_MANAGE,
        label: t("layout.DATAManage"),
        hasPermission: hasPermission(curUser, [UserPermissionEnum.DATA_MANAGE_SUPPLIER]),
        children: [
          {
            key: `${ROUTER_KEYS.DATA_MANAGE}?pageId=${PageId.DATA_MANAGE_SUPPLIER}`,
            label: t("layout.supplierMana"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.DATA_MANAGE_SUPPLIER]),
          },
        ],
      },
      {
        key: "/home/reimburse",
        label: t("layout.reimburseMana"),
        hasPermission: true,
        children: [
          {
            key: `${ROUTER_KEYS.REIMBURSE_MINE}?pageId=${PageId.REIMBURSE_MINE}`,
            label: t("layout.myApplication"),
            hasPermission: true
          },
          {
            key: `${ROUTER_KEYS.REIMBURSE_APPROVE}?pageId=${PageId.REIMBURSE_APPROVE}`,
            label: t("layout.pendingApproval"),
            hasPermission: true
          },
          {
            key: `${ROUTER_KEYS.REIMBURSE_ALL}?pageId=${PageId.REIMBURSE_ALL}`,
            label: t("layout.allApplications"),
            hasPermission: hasPermission(curUser, [UserPermissionEnum.REIMBURSE_ALL]),
          },
        ],
      },
      {
        key: `${ROUTER_KEYS.EQUIPMENT}?pageId=${PageId.EQUIPMENT}`,
        label: t("layout.equipmentManagement"),
        hasPermission: hasPermission(curUser, [UserPermissionEnum.EQUIPMENT_MANAGEMENT]),
      },
    ],
    [t, curUser, rootCategories]
  );
  return menuNavTitles;
};

export default useNav;
